import { ProgramGrades } from 'shared/lib/grades';

import { Assignment, AssignmentContent } from '../../assignment-service';
import { LessonContent, TaughtLesson } from '../../class-stats';

export type LessonStatusForEventPayload =
  | 'started-class'
  | 'assigned'
  | 'scheduled';

export type CommonXApiContextPayload = {
  result: {
    isRightClick: boolean;
    url: string;
    page: string;
    section?: string;
    classBk?: string;
    classGrade?: string | string[];
  };
};

export type XApiLessonDataProps = CommonXApiContextPayload['result'] & {
  classBk: string;
  classGrade: string[];
  content: AssignmentContent | LessonContent;
  lessonStatus: LessonStatusForEventPayload;
  assignmentBusinessKey: string;
};

export type LessonCardDataForEventPayload =
  CommonXApiContextPayload['result'] & {
    classBk: string;
    classGrade: string[];
    assignmentBusinessKey: string;
    activityId?: string;
    editionId?: string; // Placeholder. Will be used in future for desmos assignments.
    instanceId?: string;
    domain?: string;
    assignmentLesson?: string;
    unitBusinessKey?: string;
    unitRevisionAlias?: string;
    unitRevisionNumber?: number;
    chapterBusinessKey?: string;
    lessonBusinessKey?: string;
    googleSlideDeckId?: string;
    customContentId?: string;
    isCustom: boolean;
    lessonStatus: LessonStatusForEventPayload;
  };

export type GoToLessonContextEventPayload = {
  result: LessonCardDataForEventPayload;
};

export type ViewWorkContextEventPayload = {
  result: LessonCardDataForEventPayload;
};

export type ProductBreadcrumbContextEventPayload = {
  result: LessonCardDataForEventPayload;
};

export type UnitBreadcrumbContextEventPayload = {
  result: LessonCardDataForEventPayload;
};

export type LessonBreadcrumbContextEventPayload = {
  result: LessonCardDataForEventPayload;
};

export type IsRightClickContextEventPayload = {
  result: {
    isRightClick: boolean;
  };
};

export type UrlContextEventPayload = {
  result: {
    url: string;
  };
};

export type PageContextEventPayload = {
  result: {
    page: string;
  };
};

export type ProgramSelectedContextEventPayload = {
  result: {
    product: string;
  };
};

export type RecommendationItemContextEventsPayload = {
  result: {
    recommendationId: string;
    isDynamic: boolean;
    product: string;
    recommendationSource: 'educator-home' | 'see-all';
  };
};

export type ProductFilterResultEventsPayload = {
  result: {
    productFiltersApplied: string[];
    productFilterIsAll: boolean;
  };
};

export type ClassFilterResultEventsPayload = {
  result: {
    classFiltersApplied?: string[];
    classFilterIsAll?: boolean;
  };
};

export enum ASSIGNMENT_LEVELS {
  CARDSTACK = 'cardstack',
  LESSON = 'lesson',
}

export type AssignmentActionsResultEventsPayload = {
  result: {
    assignedCardstackBusinessKeys?: string[]; // array of bks of cardstacks
    assignedGroupBusinessKeys: string[]; // array of bks of assigned classes
    groupCount: number; // number of classes related to assignment
    assignmentBusinessKeys: string;
    assignmentLevel: ASSIGNMENT_LEVELS;
    noDueDate: boolean;
    dueDate: string | null; // ISO string date in format 'YYYY-MM-DD'
    dueTime: string | null; // ISO string time in format 'H-m-s' e.g. '21-30-0'
    dueDateCurrentDate?: number; // difference in days between the current date and the due date
    lessonCount?: number;
    scheduledDate: string | null; // ISO string date in format 'YYYY-MM-DD'
    scheduledTime: string | null; // ISO string time in format 'H-m-s' e.g. '21-30-0'
    scheduledDateCurrentDate?: number; // difference in days between the current date and the scheduled date
    scheduledImmediately: boolean;
  };
};

export type AssignmentActionsContextEventsPayload = {
  result: LessonCardDataForEventPayload;
};

export type UpdateAssignmentResultEventsPayload =
  AssignmentActionsResultEventsPayload & {
    result: {
      changedScheduledDate: boolean;
      changedScheduledTime: boolean;
      changedDueDate: boolean;
      changedDueTime: boolean;
    };
  };

export type CommonLiteracyInsightsContextEventsPayload = {
  result: {
    recommendationId: string;
    isDynamic: boolean;
  };
};

export type LiteracyInsightsModalOptionContextEventsPayload =
  CommonLiteracyInsightsContextEventsPayload & {
    result: {
      optionName: string;
    };
  };

export type LinkCommonResultEventsPayload = {
  result: {
    destinationUrl: string;
  };
};

export type QuickLinkContextEventsPayload = {
  result: {
    quickLinkName: string;
  };
};

export enum XApiEventKeys {
  viewed = 'viewed',
  openedAvatarBuilder = 'opened-avatar-builder',
  classFiltered = 'class-filtered',
  programsListExpanded = 'programs-list-expanded',
  programsListCollapsed = 'programs-list-collapsed',
  programSelected = 'program-selected',
  goToLessonSelected = 'go-to-lesson-selected',
  viewWork = 'view-work',
  assignmentUnassignSelected = 'assignment-unassign-selected',
  assignmentEditSelected = 'assignment-edit-selected',
  productBreadcrumbSelected = 'product-breadcrumb-selected',
  recommendationProductBreadcrumbSelected = 'recommendation-product-breadcrumb-selected',
  unitBreadcrumbSelected = 'unit-breadcrumb-selected',
  lessonBreadcrumbSelected = 'lesson-breadcrumb-selected',
  sectionInfoHovered = 'section-info-hovered',
  seeAllButtonSelected = 'see-all-button-selected',
  backButtonSelected = 'back-button-selected',
  openedRecommendation = 'recommendation-opened',
  productFiltered = 'product-filtered',
  assignmentReassignSelected = 'assignment-reassign-selected',
  selectedUndoLessonUnassign = 'selected-undo-lesson-unassign',
  selectedUndoLessonReassign = 'selected-undo-lesson-reassign',
  selectedUpdateAssignment = 'selected-update-assignment',
  selectedLiteracyInsightsTooltip = 'selected-literacy-insights-tooltip',
  selectedTLiteracyInsightsTooltipLink = 'selected-literacy-insights-tooltip-link',
  selectedLiteracyInsightsModalOption = 'selected-literacy-insights-modal-option',
  selectedLiteracyInsightsModalLink = 'selected-literacy-insights-modal-link',
  closedLiteracyInsightsModal = 'closed-literacy-insights-modal',
  launchedWalkthrough = 'launched-walkthrough',
  selectedQuickLink = 'selected-quick-link',
}

export type XApiResultExtensions = {
  [XApiEventKeys.viewed]: never;
  [XApiEventKeys.openedAvatarBuilder]: never;
  [XApiEventKeys.programsListExpanded]: never;
  [XApiEventKeys.programsListCollapsed]: never;
  [XApiEventKeys.programSelected]: never;
  [XApiEventKeys.sectionInfoHovered]: never;
  [XApiEventKeys.goToLessonSelected]: never;
  [XApiEventKeys.viewWork]: never;
  [XApiEventKeys.productBreadcrumbSelected]: never;
  [XApiEventKeys.recommendationProductBreadcrumbSelected]: never;
  [XApiEventKeys.unitBreadcrumbSelected]: never;
  [XApiEventKeys.lessonBreadcrumbSelected]: never;
  [XApiEventKeys.seeAllButtonSelected]: never;
  [XApiEventKeys.backButtonSelected]: never;
  [XApiEventKeys.openedRecommendation]: never;
  [XApiEventKeys.classFiltered]: ClassFilterResultEventsPayload;
  [XApiEventKeys.productFiltered]: ProductFilterResultEventsPayload;
  [XApiEventKeys.assignmentEditSelected]: AssignmentActionsResultEventsPayload;
  [XApiEventKeys.assignmentUnassignSelected]: AssignmentActionsResultEventsPayload;
  [XApiEventKeys.assignmentReassignSelected]: AssignmentActionsResultEventsPayload;
  [XApiEventKeys.selectedUndoLessonUnassign]: AssignmentActionsResultEventsPayload;
  [XApiEventKeys.selectedUndoLessonReassign]: AssignmentActionsResultEventsPayload;
  [XApiEventKeys.selectedUpdateAssignment]: UpdateAssignmentResultEventsPayload;
  [XApiEventKeys.selectedLiteracyInsightsTooltip]: never;
  [XApiEventKeys.selectedTLiteracyInsightsTooltipLink]: LinkCommonResultEventsPayload;
  [XApiEventKeys.selectedLiteracyInsightsModalOption]: never;
  [XApiEventKeys.selectedLiteracyInsightsModalLink]: LinkCommonResultEventsPayload;
  [XApiEventKeys.closedLiteracyInsightsModal]: never;
  [XApiEventKeys.launchedWalkthrough]: never;
  [XApiEventKeys.selectedQuickLink]: LinkCommonResultEventsPayload;
};

export type XApiContextExtensions = {
  [XApiEventKeys.goToLessonSelected]: GoToLessonContextEventPayload;
  [XApiEventKeys.viewWork]: ViewWorkContextEventPayload;
  [XApiEventKeys.productBreadcrumbSelected]: ProductBreadcrumbContextEventPayload;
  [XApiEventKeys.recommendationProductBreadcrumbSelected]: CommonXApiContextPayload;
  [XApiEventKeys.unitBreadcrumbSelected]: UnitBreadcrumbContextEventPayload;
  [XApiEventKeys.lessonBreadcrumbSelected]: LessonBreadcrumbContextEventPayload;
  [XApiEventKeys.programSelected]: ProgramSelectedContextEventPayload;
  [XApiEventKeys.sectionInfoHovered]: CommonXApiContextPayload;
  [XApiEventKeys.seeAllButtonSelected]: CommonXApiContextPayload;
  [XApiEventKeys.backButtonSelected]: CommonXApiContextPayload;
  [XApiEventKeys.productFiltered]: CommonXApiContextPayload;
  [XApiEventKeys.classFiltered]: CommonXApiContextPayload;
  [XApiEventKeys.openedRecommendation]: RecommendationItemContextEventsPayload;
  [XApiEventKeys.assignmentEditSelected]: AssignmentActionsContextEventsPayload;
  [XApiEventKeys.assignmentUnassignSelected]: AssignmentActionsContextEventsPayload;
  [XApiEventKeys.assignmentReassignSelected]: AssignmentActionsContextEventsPayload;
  [XApiEventKeys.selectedUndoLessonUnassign]: AssignmentActionsContextEventsPayload;
  [XApiEventKeys.selectedUndoLessonReassign]: AssignmentActionsContextEventsPayload;
  [XApiEventKeys.selectedUpdateAssignment]: AssignmentActionsContextEventsPayload;
  [XApiEventKeys.selectedLiteracyInsightsTooltip]: never;
  [XApiEventKeys.selectedTLiteracyInsightsTooltipLink]: never;
  [XApiEventKeys.selectedLiteracyInsightsModalOption]: LiteracyInsightsModalOptionContextEventsPayload;
  [XApiEventKeys.selectedLiteracyInsightsModalLink]: CommonLiteracyInsightsContextEventsPayload;
  [XApiEventKeys.closedLiteracyInsightsModal]: CommonLiteracyInsightsContextEventsPayload;
  [XApiEventKeys.launchedWalkthrough]: never;
  [XApiEventKeys.selectedQuickLink]: QuickLinkContextEventsPayload;
  isRightClick: IsRightClickContextEventPayload;
  page: PageContextEventPayload;
  url: UrlContextEventPayload;
};

export type DefaultClickProps = CommonXApiContextPayload['result'] & {
  lessonStatus?: LessonStatusForEventPayload;
};

export type AssignmentActionsClickProps = DefaultClickProps & {
  eventType: XApiEventKeys;
  assignment: Assignment | TaughtLesson;
};

export type GenerateCardEventHandlersOptions = {
  currentClassGrades: ProgramGrades[];
  item: Assignment | TaughtLesson;
};

export type CardEventHandlers = {
  onGoToLessonClick: (props: DefaultClickProps) => Promise<unknown>;
  onViewWorkClick: (props: DefaultClickProps) => Promise<unknown>;
  onProductBreadcrumbClick: (props: DefaultClickProps) => Promise<unknown>;
  onUnitBreadcrumbClick: (props: DefaultClickProps) => Promise<unknown>;
  onLessonBreadcrumbClick: (props: DefaultClickProps) => Promise<unknown>;
  onAssignmentActionsClick: (
    props: AssignmentActionsClickProps,
  ) => Promise<unknown>;
};

export type GenerateAssignmentActionsContextEventPayloadOptions =
  CommonXApiContextPayload['result'] & {
    item: Assignment;
    currentClassGrades: ProgramGrades[];
    currentClassesBks: string[];
    lessonStatus?: LessonStatusForEventPayload | undefined;
  };

export type GenerateAssignmentActionsResultEventPayloadOptions = {
  eventType: XApiEventKeys;
  assignmentType: 'desmos' | 'Lesson' | 'Cardstack';
  content: AssignmentContent | AssignmentContent[];
  assignmentBusinessKeys: string;
  assignmentClassBks: string[];
  assignmentStartDate?: number | null;
  assignmentDueDate?: number | null;
};

export type GenerateUpdateAssignmentResultEventsPayload = {
  assignment: Assignment;
  newStartDate: number | null | undefined;
  newDueDate: number | null | undefined;
};
